import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Layers } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { buttonVariants } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatDisplayDateTime } from '@/utils/date';
import { formatNumber, formatTaka } from '@/utils/number';
import {
  downloadPcmpMarketingBatchXlsx,
  pcmpMarketingBatchesQueryOptions,
} from '../../api/pcmpMarketing.api';
import { PCMP_CHANNEL_LABELS } from '../../constants/pcmpProposal.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import { toPcmpQueueSearch } from '../../hooks/usePcmpQueueFilters';
import type {
  PcmpMarketingBatch,
  PcmpMarketingBatches,
  PcmpMarketingBatchesParams,
} from '../../types/pcmpMarketing.types';
import type { PcmpQueueFilters } from '../../types/pcmpQueue.types';
import { PcmpBatchAmountCell } from '../PcmpBatch/PcmpBatchCells';
import { PcmpBatchStatusPill } from '../PcmpMisBatches/PcmpMisBatchCells';
import { PcmpEmptyCell } from '../PcmpQueue/PcmpQueueCells';
import { PcmpExportButton } from '../PcmpQueue/PcmpExportButton';

function TotalsStrip({ totals }: { totals: PcmpMarketingBatches['totals'] }) {
  const items = [
    { label: 'Total', value: totals.total },
    { label: 'Disbursed', value: totals.disbursed },
    { label: 'Pending', value: totals.pending },
    { label: 'Disb. Failed', value: totals.disbursementFailed },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {items.map(({ label, value }) => (
        <div key={label} className="flex flex-col rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className="text-lg font-semibold tabular-nums">{formatTaka(value.amount)}</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {formatNumber(value.count)} proposals
          </span>
        </div>
      ))}
    </div>
  );
}

const amountColumn = (
  id: string,
  header: string,
  pick: (batch: PcmpMarketingBatch) => { count: number; amount: number },
): ColumnDef<PcmpMarketingBatch> => ({
  id,
  header: () => <div className="text-right whitespace-nowrap">{header}</div>,
  cell: ({ row }) => <PcmpBatchAmountCell value={pick(row.original)} />,
});

interface PcmpMarketingBatchesViewProps {
  params: PcmpMarketingBatchesParams;
  /** The drill-down and its Excel take the same filters. */
  appliedFilters: PcmpQueueFilters;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

/**
 * The Batches tab (legacy Proposals page): the route batches holding the
 * viewer's proposals, newest first, every figure the viewer's share. A row
 * opens the batch's proposals; its Excel holds the same rows (LDS-820).
 */
export function PcmpMarketingBatchesView({
  params,
  appliedFilters,
  onPageChange,
  onLimitChange,
}: PcmpMarketingBatchesViewProps) {
  const router = useRouter();

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpMarketingBatchesQueryOptions(params),
  );

  useKeepPageInRange(params.page, data?.batches.meta.totalPages, onPageChange);

  const search = useMemo(() => toPcmpQueueSearch(appliedFilters), [appliedFilters]);

  const openBatch = useCallback(
    (batch: PcmpMarketingBatch) => {
      void router.navigate({
        to: '/pcmp/marketing/batches/$batchNo',
        params: { batchNo: batch.batchNo },
        search,
      });
    },
    [router, search],
  );

  const columns = useMemo<ColumnDef<PcmpMarketingBatch>[]>(
    () => [
      {
        id: 'batchNo',
        header: 'Batch',
        cell: ({ row }) => (
          <span className="font-mono font-medium whitespace-nowrap">#{row.original.batchNo}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <PcmpBatchStatusPill status={row.original.batchStatus} />,
      },
      {
        id: 'channel',
        header: 'Payer',
        cell: ({ row }) =>
          row.original.channel ? PCMP_CHANNEL_LABELS[row.original.channel] : <PcmpEmptyCell />,
      },
      {
        id: 'publishedAt',
        header: 'Published',
        cell: ({ row }) =>
          row.original.publishedAt ? (
            <span className="whitespace-nowrap">
              {formatDisplayDateTime(row.original.publishedAt)}
            </span>
          ) : (
            <PcmpEmptyCell />
          ),
      },
      amountColumn('total', 'Your Items', (batch) => batch.total),
      amountColumn('pending', 'Pending', (batch) => batch.pending),
      amountColumn('disbursed', 'Disbursed', (batch) => batch.disbursed),
      amountColumn('failed', 'Disb. Failed', (batch) => batch.disbursementFailed),
      amountColumn('held', 'On Hold', (batch) => batch.held),
      amountColumn('rejected', 'Rejected', (batch) => batch.rejected),
      amountColumn('returned', 'Returned', (batch) => batch.returned),
      {
        id: 'actions',
        header: () => <div className="text-right">Action</div>,
        cell: ({ row }) => (
          // The row itself opens the batch; keep button clicks from reaching it.
          <div
            className="flex justify-end gap-1.5"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            role="presentation"
          >
            <Link
              to="/pcmp/marketing/batches/$batchNo"
              params={{ batchNo: row.original.batchNo }}
              search={search}
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
            >
              <Eye className="size-3.5" />
              View
            </Link>
            <PcmpExportButton
              title={`Batch #${row.original.batchNo}: your proposals in it, under the filters above`}
              download={(date) =>
                downloadPcmpMarketingBatchXlsx(row.original.batchNo, appliedFilters, date)
              }
            />
          </div>
        ),
      },
    ],
    [appliedFilters, search],
  );

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load the batches"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        fill
      />
    );
  }

  return (
    <>
      {data ? <TotalsStrip totals={data.totals} /> : null}
      <DataTable
        data={data?.batches.items ?? []}
        columns={columns}
        isLoading={isLoading}
        stickyFirstColumn
        fillHeight
        className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
        emptyIcon={Layers}
        emptyMessage="No batches hold your proposals"
        skeletonRowCount={Math.min(params.limit, 15)}
        onRowClick={openBatch}
      />
      {data ? (
        <DataTablePagination
          page={data.batches.meta.page}
          totalPages={data.batches.meta.totalPages}
          total={data.batches.meta.total}
          limit={data.batches.meta.limit}
          onPageChange={onPageChange}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: onLimitChange }}
        />
      ) : null}
    </>
  );
}
